import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import type { User } from "@supabase/supabase-js"
import { appLogger } from "@/lib/appLogger"
import { supabase } from "@/lib/supabase"
import { toast } from "@/hooks/use-toast"
import { recordAuthAnalytics } from "@/services/authAnalytics"
import { trackPageLoad } from "@/services/pageTracking"
import { safeRandomUUID } from "@/lib/utils"
import { ensureAuthenticatedProfileRecord } from "@/modules/village-feed/creatorFollowApi"

export interface Profile {
    id: string
    full_name: string
    role: string
    phone: string
    avatar_url: string
    is_online: boolean
}

export type AuthContextValue = {
    user: User | null
    currentUser: User | null
    profile: Profile | null
    isLoading: boolean
    isLoggingOut: boolean
    signInWithGoogle: (opts?: { trigger?: "auto" | "button"; fallbackToRedirect?: boolean }) => Promise<void>
    logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)
const AUTH_SIGN_OUT_TIMEOUT_MS = 4000
const AUTH_RECOGNITION_KEY = "auth_recognition_v1"
const AUTH_VISITOR_ID_KEY = "auth_visitor_id_v1"
const GOOGLE_ONE_TAP_CLIENT_ID = "565203109376-18l1a9nn6dq201mgeqs3f35lldq0m55n.apps.googleusercontent.com"
const DEBUG_AUTH_SESSION_ID = "sign-in-failure"
const PERF_DEBUG_SESSION_ID = "slow-page-load"

function reportAuthDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
    if (!(import.meta as any)?.env?.DEV) return
    if (typeof window === "undefined" || (window as any).__TRAE_AUTH_DEBUG__ !== true) return
    const debugAuthUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
    if (!debugAuthUrl) return
    fetch(debugAuthUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sessionId: DEBUG_AUTH_SESSION_ID,
            runId: "pre-fix",
            hypothesisId,
            location,
            msg,
            data,
            ts: Date.now(),
        }),
    }).catch(() => { })
}

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
    if (!(import.meta as any)?.env?.DEV) return
    if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
    const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
    if (!perfDebugUrl) return
    fetch(perfDebugUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            sessionId: PERF_DEBUG_SESSION_ID,
            runId: "pre-fix",
            hypothesisId,
            location,
            msg,
            data,
            ts: Date.now(),
        }),
    }).catch(() => { })
}

type AuthRecognitionRecord = {
    version: 1
    visitorId: string
    userId: string
    emailHash: string
    lastLoginAt: number
}

function readAuthRecognition(): AuthRecognitionRecord | null {
    try {
        const raw = localStorage.getItem(AUTH_RECOGNITION_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as Partial<AuthRecognitionRecord>
        if (parsed.version !== 1) return null
        if (typeof parsed.visitorId !== "string" || !parsed.visitorId) return null
        if (typeof parsed.userId !== "string" || !parsed.userId) return null
        if (typeof parsed.emailHash !== "string" || !parsed.emailHash) return null
        if (typeof parsed.lastLoginAt !== "number") return null
        return parsed as AuthRecognitionRecord
    } catch {
        return null
    }
}

function writeAuthRecognition(rec: AuthRecognitionRecord) {
    try {
        localStorage.setItem(AUTH_RECOGNITION_KEY, JSON.stringify(rec))
    } catch {
        void 0
    }
}

function clearAuthRecognition() {
    try {
        localStorage.removeItem(AUTH_RECOGNITION_KEY)
    } catch {
        void 0
    }
}

function readCachedVisitorId(): string {
    try {
        return String(localStorage.getItem(AUTH_VISITOR_ID_KEY) || "")
    } catch {
        return ""
    }
}

async function sha256Base64Url(input: string): Promise<string> {
    const normalized = String(input || "").trim().toLowerCase()
    if (!normalized) return ""
    try {
        if (!globalThis.crypto?.subtle) return normalized
        const bytes = new TextEncoder().encode(normalized)
        const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes)
        const arr = Array.from(new Uint8Array(digest))
        const b64 = btoa(String.fromCharCode(...arr))
        return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "")
    } catch {
        return normalized
    }
}

async function getOrCreateVisitorId(): Promise<string> {
    const fingerprintStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
    try {
        const cached = localStorage.getItem(AUTH_VISITOR_ID_KEY)
        if (cached) {
            // #region debug-point B:visitor-id-cache-hit
            reportPerfDebug("B", "src/contexts/AuthContext.tsx:130", "[DEBUG] Visitor ID cache hit", {
                elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - fingerprintStartMs,
            })
            // #endregion
            return cached
        }
    } catch {
        void 0
    }
    const FingerprintJS = (await import("@fingerprintjs/fingerprintjs")).default
    const fp = await FingerprintJS.load()
    const result = await fp.get()
    const visitorId = result.visitorId || safeRandomUUID()
    try {
        localStorage.setItem(AUTH_VISITOR_ID_KEY, visitorId)
    } catch {
        void 0
    }
    // #region debug-point B:visitor-id-generated
    reportPerfDebug("B", "src/contexts/AuthContext.tsx:144", "[DEBUG] Visitor ID generated via FingerprintJS", {
        elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - fingerprintStartMs,
        hasVisitorId: Boolean(visitorId),
    })
    // #endregion
    return visitorId
}

type OneTapOutcome =
    | { type: "success" }
    | { type: "unavailable" }
    | { type: "dismissed"; reason?: string }
    | { type: "error"; message: string }

type GoogleOneTapCredentialResponse = {
    credential?: string
    select_by?: string
    clientId?: string
}

type GooglePromptMomentNotification = {
    isNotDisplayed: () => boolean
    isSkippedMoment: () => boolean
    isDismissedMoment: () => boolean
    getNotDisplayedReason: () => string
    getSkippedReason: () => string
    getDismissedReason: () => string
}

declare global {
    interface Window {
        google?: {
            accounts?: {
                id?: {
                    initialize: (opts: {
                        client_id: string
                        callback: (response: GoogleOneTapCredentialResponse) => void
                        auto_select?: boolean
                        cancel_on_tap_outside?: boolean
                        itp_support?: boolean
                        use_fedcm_for_prompt?: boolean
                    }) => void
                    prompt: (momentListener?: (n: GooglePromptMomentNotification) => void) => void
                    cancel: () => void
                }
            }
        }
    }
}

let googleGsiLoadPromise: Promise<void> | null = null
function loadGoogleIdentityScript(): Promise<void> {
    if (typeof document === "undefined") return Promise.reject(new Error("Document is not available"))
    if (window.google?.accounts?.id) return Promise.resolve()
    if (googleGsiLoadPromise) return googleGsiLoadPromise

    googleGsiLoadPromise = new Promise<void>((resolve, reject) => {
        let settled = false
        const settleResolve = () => {
            if (settled) return
            settled = true
            resolve()
        }
        const settleReject = (err: Error) => {
            if (settled) return
            settled = true
            reject(err)
        }

        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]')
        if (existing) {
            globalThis.setTimeout(() => {
                if (window.google?.accounts?.id) settleResolve()
                else settleReject(new Error("Google Identity Services did not initialize"))
            }, 2000)
            return
        }

        const script = document.createElement("script")
        script.src = "https://accounts.google.com/gsi/client"
        script.async = true
        script.defer = true
        script.onload = () => settleResolve()
        script.onerror = () => settleReject(new Error("Failed to load Google Identity Services"))
        document.head.appendChild(script)

        globalThis.setTimeout(() => {
            if (!window.google?.accounts?.id) settleReject(new Error("Google Identity Services load timed out"))
        }, 3500)
    })

    return googleGsiLoadPromise
}

async function tryOneTapSignIn(trigger: "auto" | "button"): Promise<OneTapOutcome> {
    try {
        await loadGoogleIdentityScript()
    } catch (e) {
        return { type: "unavailable" }
    }

    const gsi = window.google?.accounts?.id
    if (!gsi) return { type: "unavailable" }

    return await new Promise<OneTapOutcome>((resolve) => {
        let settled = false
        const settle = (out: OneTapOutcome) => {
            if (settled) return
            settled = true
            resolve(out)
        }

        const timeout = globalThis.setTimeout(() => settle({ type: "unavailable" }), trigger === "auto" ? 2800 : 1800)

        try {
            gsi.initialize({
                client_id: GOOGLE_ONE_TAP_CLIENT_ID,
                callback: async (resp: GoogleOneTapCredentialResponse) => {
                    try {
                        if (!resp?.credential) {
                            globalThis.clearTimeout(timeout)
                            settle({ type: "error", message: "Missing credential from Google One Tap" })
                            return
                        }
                        const { error } = await supabase.auth.signInWithIdToken({
                            provider: "google",
                            token: resp.credential,
                        })
                        // #region debug-point C:one-tap-id-token
                        reportAuthDebug("C", "src/contexts/AuthContext.tsx:225", "[DEBUG] One Tap signInWithIdToken completed", { trigger, hasCredential: Boolean(resp?.credential), error: error?.message || null })
                        // #endregion
                        globalThis.clearTimeout(timeout)
                        if (error) {
                            settle({ type: "error", message: error.message || "One Tap sign-in failed" })
                            return
                        }
                        settle({ type: "success" })
                    } catch (err) {
                        globalThis.clearTimeout(timeout)
                        const msg = err instanceof Error ? err.message : "One Tap sign-in failed"
                        settle({ type: "error", message: msg })
                    }
                },
                auto_select: true,
                cancel_on_tap_outside: false,
                itp_support: true,
                use_fedcm_for_prompt: true,
            })

            gsi.prompt((n: GooglePromptMomentNotification) => {
                if (!n) return
                if (n.isNotDisplayed()) {
                    globalThis.clearTimeout(timeout)
                    settle({ type: "dismissed", reason: n.getNotDisplayedReason?.() })
                    return
                }
                if (n.isSkippedMoment()) {
                    globalThis.clearTimeout(timeout)
                    settle({ type: "dismissed", reason: n.getSkippedReason?.() })
                    return
                }
                if (n.isDismissedMoment()) {
                    globalThis.clearTimeout(timeout)
                    settle({ type: "dismissed", reason: n.getDismissedReason?.() })
                }
            })
        } catch (err) {
            globalThis.clearTimeout(timeout)
            const msg = err instanceof Error ? err.message : "Google One Tap initialization failed"
            settle({ type: "error", message: msg })
        }
    })
}

export function AuthProvider({ children }: { children: React.ReactNode }) {

    const [user, setUser] = useState<User | null>(null)
    const [currentUser, setCurrentUser] = useState<User | null>(null)
    const [profile, setProfile] = useState<Profile | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isLoggingOut, setIsLoggingOut] = useState(false)

    const clearClientAuthState = useCallback(() => {
        try {
            const keysToRemove: string[] = []

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i)
                if (key && (key.startsWith("sb-") || key.startsWith("samvaad_"))) {
                    keysToRemove.push(key)
                }
            }

            keysToRemove.forEach((key) => localStorage.removeItem(key))
            localStorage.removeItem(AUTH_RECOGNITION_KEY)
            localStorage.removeItem(AUTH_VISITOR_ID_KEY)
        } catch (err) {
            console.warn("[AuthContext] localStorage cleanup failed:", err)
        }

        setCurrentUser(null)
        setUser(null)
        setProfile(null)
    }, [])

    /**
     * Fetch profile safely
     */
    const fetchProfile = useCallback(async (userId: string) => {
        void userId
        setProfile(null)
    }, [])

    /**
     * Initialize session on first load
     */
    useEffect(() => {
        let cancelled = false

        const init = async () => {
            const authInitStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
            try {
                let visitorId = readCachedVisitorId()
                const { data, error } = await supabase.auth.getSession()
                // #region debug-point B:auth-init-session
                reportPerfDebug("B", "src/contexts/AuthContext.tsx:377", "[DEBUG] Auth init session fetched", {
                    hasVisitorId: Boolean(visitorId),
                    hasSession: Boolean(data.session),
                    userId: data.session?.user?.id || null,
                    elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - authInitStartMs,
                    error: error?.message || null,
                })
                // #endregion
                // #region debug-point E:init-get-session
                reportAuthDebug("E", "src/contexts/AuthContext.tsx:338", "[DEBUG] Initial session fetch completed", {
                    hasVisitorId: Boolean(visitorId),
                    hasSession: Boolean(data.session),
                    userId: data.session?.user?.id || null,
                    error: error?.message || null,
                })
                // #endregion
                if (cancelled) return
                if (error) {
                    if (typeof navigator !== "undefined" && navigator.onLine === false) {
                        toast({ variant: "destructive", title: "Network error", description: "You appear to be offline." })
                    } else {
                        toast({ variant: "destructive", title: "Sign-in unavailable", description: "Authentication is temporarily unavailable." })
                    }
                    setIsLoading(false)
                    return
                }

                let session = data.session
                if (!session) {
                    try {
                        const refreshed = await supabase.auth.refreshSession()
                        session = refreshed.data.session
                        // #region debug-point E:init-refresh-session
                        reportAuthDebug("E", "src/contexts/AuthContext.tsx:353", "[DEBUG] Session refresh attempted during init", {
                            hasSession: Boolean(refreshed.data.session),
                            userId: refreshed.data.session?.user?.id || null,
                            error: refreshed.error?.message || null,
                        })
                        // #endregion
                    } catch {
                        session = null
                    }
                }

                const nextUser = session?.user || null

                if (nextUser) {
                    const rec = readAuthRecognition()
                    if (rec && !visitorId) {
                        visitorId = await getOrCreateVisitorId().catch(() => "")
                    }
                    // #region debug-point D:recognition-check
                    reportAuthDebug("D", "src/contexts/AuthContext.tsx:366", "[DEBUG] Recognition record evaluated", {
                        hasRecognitionRecord: Boolean(rec),
                        visitorId,
                        recognizedVisitorId: rec?.visitorId || null,
                        nextUserId: nextUser.id,
                        mismatch: Boolean(rec && visitorId && rec.visitorId !== visitorId),
                    })
                    // #endregion
                    if (rec && visitorId && rec.visitorId !== visitorId) {
                        try {
                            await supabase.auth.signOut({ scope: "local" })
                        } catch {
                            void 0
                        }
                        clearClientAuthState()
                        setIsLoading(false)
                        return
                    }
                }

                setUser(nextUser)
                setCurrentUser(nextUser)
                if (nextUser) {
                    fetchProfile(nextUser.id).catch(() => { })
                } else {
                    setProfile(null)
                }
                // #region debug-point B:auth-init-complete
                reportPerfDebug("B", "src/contexts/AuthContext.tsx:447", "[DEBUG] Auth init completed", {
                    hasUser: Boolean(nextUser),
                    userId: nextUser?.id || null,
                    elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - authInitStartMs,
                })
                // #endregion
                setIsLoading(false)
            } catch (e) {
                console.warn("[AuthContext] init failed:", e)
                // #region debug-point B:auth-init-failed
                reportPerfDebug("B", "src/contexts/AuthContext.tsx:455", "[DEBUG] Auth init failed", {
                    message: e instanceof Error ? e.message : String(e),
                    elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - authInitStartMs,
                })
                // #endregion
                setIsLoading(false)
            }
        }

        void init()

        const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
            // #region debug-point D:auth-state-change
            reportAuthDebug("D", "src/contexts/AuthContext.tsx:401", "[DEBUG] onAuthStateChange fired", { event, hasSession: Boolean(session), hasAccessToken: Boolean(session?.access_token), userId: session?.user?.id || null })
            // #endregion
            const nextUser = session?.user || null
            setUser(nextUser)
            setCurrentUser(nextUser)
            if (nextUser) {
                fetchProfile(nextUser.id).catch(() => { })
            } else {
                setProfile(null)
            }

            if (event === "SIGNED_IN") {
                try {
                    const hasToken = Boolean(session?.access_token)
                    appLogger.info("Signed in", { category: "auth", meta: { hasToken } })
                } catch {
                    void 0
                }
                if (nextUser?.id) {
                    void (async () => {
                        try {
                            const key = "auth.last_profile_sync_user"
                            let last = ""
                            try { last = String(sessionStorage.getItem(key) || "") } catch { void 0 }
                            if (last !== String(nextUser.id)) {
                                await ensureAuthenticatedProfileRecord(nextUser)
                                try { sessionStorage.setItem(key, String(nextUser.id)) } catch { void 0 }
                            }
                        } catch {
                            void 0
                        }
                    })()
                }
                void (async () => {
                    const visitorId = await getOrCreateVisitorId().catch(() => "")
                    const emailHash = await sha256Base64Url(nextUser?.email || "")
                    if (!visitorId || !nextUser?.id || !emailHash) return
                    writeAuthRecognition({
                        version: 1,
                        visitorId,
                        userId: nextUser.id,
                        emailHash,
                        lastLoginAt: Date.now(),
                    })
                })()
                recordAuthAnalytics("SIGNED_IN").catch(() => { })
                trackPageLoad({ pageType: "auth_login" }).catch(() => { })
                try {
                    const next = localStorage.getItem("post_login_next")
                    if (next && next.startsWith("/")) {
                        localStorage.removeItem("post_login_next")
                        window.location.replace(next)
                    }
                } catch {
                    void 0
                }
            } else if (event === "TOKEN_REFRESHED") {
                try {
                    const hasToken = Boolean(session?.access_token)
                    appLogger.info("Token refreshed", { category: "auth", meta: { hasToken } })
                } catch {
                    void 0
                }
                void (async () => {
                    const visitorId = await getOrCreateVisitorId().catch(() => "")
                    const emailHash = await sha256Base64Url(nextUser?.email || "")
                    if (!visitorId || !nextUser?.id || !emailHash) return
                    writeAuthRecognition({
                        version: 1,
                        visitorId,
                        userId: nextUser.id,
                        emailHash,
                        lastLoginAt: Date.now(),
                    })
                })()
                recordAuthAnalytics("TOKEN_REFRESHED").catch(() => { })
            } else if (event === "USER_UPDATED") {
                recordAuthAnalytics("USER_UPDATED").catch(() => { })
            } else if (event === "SIGNED_OUT") {
                recordAuthAnalytics("UNKNOWN").catch(() => { })
                trackPageLoad({ pageType: "auth_logout" }).catch(() => { })
            }
        })

        return () => {
            cancelled = true
            listener.subscription.unsubscribe()
        }

    }, [fetchProfile])

    /**
     * Google OAuth login
     */
    const signInWithGoogle = useCallback(async (opts?: { trigger?: "auto" | "button"; fallbackToRedirect?: boolean }) => {

        const trigger: "auto" | "button" = opts?.trigger ?? "button"
        const fallbackToRedirect = opts?.fallbackToRedirect ?? (trigger === "button")

        setIsLoading(true)

        try {
            appLogger.info("Sign-in attempt started", { category: "auth", meta: { trigger } })
            const visitorId = await getOrCreateVisitorId().catch(() => "")
            const rec = readAuthRecognition()
            const returningDevice = Boolean(visitorId && rec?.visitorId === visitorId)

            const oneTap = await tryOneTapSignIn(trigger)
            // #region debug-point B:one-tap-result
            reportAuthDebug("B", "src/contexts/AuthContext.tsx:496", "[DEBUG] One Tap attempt resolved", { trigger, returningDevice, oneTapType: oneTap?.type || null, reason: ("reason" in (oneTap || {}) ? (oneTap as any).reason || null : null), message: ("message" in (oneTap || {}) ? (oneTap as any).message || null : null) })
            // #endregion
            if (oneTap.type === "success") {
                setIsLoading(false)
                return
            }

            if (oneTap.type === "error" && trigger === "button") {
                toast({
                    variant: "destructive",
                    title: "Google One Tap failed",
                    description: oneTap.message,
                })
            }

            if (oneTap.type === "dismissed" && trigger === "button") {
                toast({
                    title: "Google One Tap dismissed",
                    description: "Continuing with Google sign-in…",
                })
            }

            if (oneTap.type === "unavailable" && trigger === "button") {
                toast({
                    title: "Google One Tap unavailable",
                    description: "Continuing with Google sign-in…",
                })
            }

            if (!fallbackToRedirect) {
                setIsLoading(false)
                return
            }

            try {
                const nextPath = `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`
                if (nextPath && nextPath.startsWith("/")) {
                    localStorage.setItem("post_login_next", nextPath)
                }
            } catch {
                void 0
            }

            const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                    scopes: "openid email"
                }
            })
            // #region debug-point A:oauth-start
            reportAuthDebug("A", "src/contexts/AuthContext.tsx:541", "[DEBUG] OAuth redirect initiated", { trigger, returningDevice, redirectTo: `${window.location.origin}/auth/callback`, prompt: "default", error: error?.message || null, origin: window.location.origin })
            // #endregion

            if (error) {
                console.error("[AuthContext] Google login error:", error)
                throw error
            }
        } catch (error) {
            // #region debug-point C:sign-in-catch
            reportAuthDebug("C", "src/contexts/AuthContext.tsx:549", "[DEBUG] signInWithGoogle caught error", { trigger: opts?.trigger ?? "button", message: error instanceof Error ? error.message : String((error as any)?.message || error), name: error instanceof Error ? error.name : null })
            // #endregion
            console.error("[AuthContext] Sign in failed:", error)
            setIsLoading(false)
            const message = (() => {
                if (error instanceof Error) return error.message
                if (error && typeof error === "object" && "message" in error) {
                    const maybeMessage = (error as { message?: unknown }).message
                    if (typeof maybeMessage === "string") return maybeMessage
                }
                return "Failed to initiate Google Sign In"
            })()
            toast({
                variant: "destructive",
                title: "Sign in failed",
                description: message
            })
        }
    }, [])

    /**
     * Logout (bulletproof)
     */
    const logout = useCallback(async () => {

        setIsLoading(true)
        setIsLoggingOut(true)
        let forcedLocalCleanup = false
        let lastErrorMessage = ""

        try {
            const signOutResult = await Promise.race([
                supabase.auth.signOut(),
                new Promise<{ error: { message: string } }>((resolve) => {
                    window.setTimeout(() => {
                        resolve({ error: { message: "Sign out request timed out" } })
                    }, AUTH_SIGN_OUT_TIMEOUT_MS)
                })
            ])
            const { error } = signOutResult

            if (error) {
                lastErrorMessage = error.message || "Unknown sign out error"
                console.error("[AuthContext] signOut error, retrying local cleanup:", error)
                const localSignOutResult = await Promise.race([
                    supabase.auth.signOut({ scope: "local" }),
                    new Promise<{ error: { message: string } }>((resolve) => {
                        window.setTimeout(() => {
                            resolve({ error: { message: "Local sign out request timed out" } })
                        }, AUTH_SIGN_OUT_TIMEOUT_MS)
                    })
                ])
                const { error: localError } = localSignOutResult
                if (localError) {
                    forcedLocalCleanup = true
                    lastErrorMessage = localError.message || lastErrorMessage
                    console.error("[AuthContext] local signOut fallback failed:", localError)
                } else {
                    void 0
                }
            } else {
                void 0
            }

        } catch (error) {
            forcedLocalCleanup = true
            lastErrorMessage = error instanceof Error ? error.message : "Unexpected logout failure"
            console.error("[AuthContext] Critical error logging out:", error)
        } finally {
            clearAuthRecognition()
            clearClientAuthState()
            setIsLoggingOut(false)
            setIsLoading(false)

            if (forcedLocalCleanup) {
                toast({
                    variant: "destructive",
                    title: "Signed out on this device",
                    description: lastErrorMessage || "Remote logout could not be fully confirmed."
                })
            }

            window.location.replace('/')
        }
    }, [clearClientAuthState])

    /**
     * Memoized context value
     */
    const value = useMemo<AuthContextValue>(() => ({
        user,
        currentUser,
        profile,
        isLoading,
        isLoggingOut,
        signInWithGoogle,
        logout
    }), [user, currentUser, profile, isLoading, isLoggingOut, logout, signInWithGoogle])

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

/**
 * Hook
 */
export function useAuth(): AuthContextValue {

    const ctx = useContext(AuthContext)

    if (ctx) {
        return { ...ctx, user: ctx.user ?? null }
    }

    console.warn("[AuthContext] Provider missing")

    return {
        user: null,
        currentUser: null,
        profile: null,
        isLoading: false,
        isLoggingOut: false,
        signInWithGoogle: async () => { },
        logout: async () => { }
    }
}
